import React, { useEffect, useState } from "react";
import { delete_category, get_category } from "../../reduxData/user/userAction";
import EmptyList from "../../Shared/EmptyList";
import { useDispatch, useSelector } from "react-redux";
import CustomPagination from "../../components/CustomPagination";
import CategoryDetails from "../../Modals/Categories/CategoryDetails";
import AddCategoryModal from "../../Modals/Categories/AddCategoryModal";
import AddSubCategoryModal from "../../Modals/Categories/AddSubCategoryModal";
import { useTranslation } from "react-i18next";
import { languageFields } from "../../hooks/LanguageFields";
import { Pause, Play, Search, SlidersHorizontal, X } from "lucide-react";

const StatusBadge = ({ active }) => {
  const { t } = useTranslation();
  return (
    <span className={active ? "status-active" : "status-inactive"}>
      {active ? t("category.statusActive") : t("category.statusInactive")}
    </span>
  );
};

const Category = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { userPermissions } = useSelector((state) => state.auth);
  const { categoriesList, totalCategories } = useSelector((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [name, setName] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [resetPage] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const canCreateCategory = userPermissions?.categories?.create;
  const canEditCategory = userPermissions?.categories?.edit;
  const canToggleCategory = userPermissions?.categories?.delete;
  const canManageCategory = canEditCategory || canToggleCategory;

  // fetch category data 
  const fetchCategories = async (page = currentPage, rows = perPage, search = name, status = statusFilter) => {
    await get_category(dispatch, page, rows, search, status);
  };

  useEffect(() => {
    fetchCategories();
  }, [currentPage, perPage, name, statusFilter,]);

  // handle deative category toggle button 
  const handleToggleStatus = async (item) => {
    const message = item?.status ? "Category Inactive Successfully" : "Category Active Successfully";
    const res = await delete_category(item?.id, dispatch, message);
    if (res) {
      fetchCategories();
    }
  };

  const clearFilters = () => {
    setName("");
    setStatusFilter("All");
    setCurrentPage(1);
  };

  return (
    <div className="category-page">
      <div className="category-header">
        <div>
          <h2>{t("category.pageTitle")}</h2>
          <p>{t("category.pageSubtitle")}</p>
        </div>
        <div className="header-actions">
          {canCreateCategory && (
            <>
              <button
                className="secondary-btn"
                onClick={() => setAddSubOpen(true)}
              > {t("category.addSubCategory")} </button>
              <button
                className="primary-btn"
                onClick={() => setAddCatOpen(true)}
              > {t("category.addCategory")} </button>
            </>
          )}
        </div>
      </div>

      <section className="category-filter-card">
        <div className="category-filter-heading">
          <span className="category-filter-icon" aria-hidden="true">
            <SlidersHorizontal size={17} />
          </span>
          <div>
            <strong>{t("category.findCategories")}</strong>
            <small>{t("category.filterHint")}</small>
          </div>
        </div>

        <div className="category-filter-controls">
          <div className="category-filter-search">
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              aria-label={t("category.searchPlaceholder")}
              placeholder={t("category.searchPlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <label className="category-filter-field">
            <span>{t("category.statusLabel")}</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All">{t("category.statusAll")}</option>
              <option value="Active">{t("category.statusActive")}</option>
              <option value="In-active">{t("category.statusInactive")}</option>
            </select>
          </label>

          <button
            className="category-clear-filters"
            type="button"
            onClick={clearFilters}
          >
            <X size={16} aria-hidden="true" />
            {t("category.clearFilters")}
          </button>
        </div>
      </section>

      <div className="category-card">
        <div className="table-responsive">
          <table className="modern-table">
            <thead>
              <tr>
                <th>{t("category.colNumber")}</th>
                <th>{t("category.colCategory")}</th>
                <th>{t("category.colSubCategories")}</th>
                <th>{t("category.colStatus")}</th>
                {canManageCategory && <th>{t("category.colActions")}</th>}
              </tr>
            </thead>

            <tbody>
              {totalCategories > 0 ? (
                categoriesList.map(
                  (item, index) => (
                    <tr key={item.id || item._id}>
                      <td>{(currentPage - 1) * perPage + index + 1}</td>
                      <td>
                        <div className="category-name-badge">
                          {languageFields(item, "name", i18n)}
                          {/* {item.name} */}
                        </div>
                      </td>
                      <td>
                        <div className="sub-chip-wrapper">
                          {(item.subcategories || []).map((sub, i) => (
                            <span className="sub-chip" key={sub.id || sub._id || i}>
                              {languageFields(sub, "name", i18n)}
                              {/* {sub.name} */}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td><StatusBadge active={item.status} /></td>
                      {canManageCategory && (
                        <td>
                          <div className="table-actions">
                            {canEditCategory && (
                              <button className="edit-btn"
                                onClick={() => setEditCategory(item)} > {t("category.edit")} </button>
                            )}
                            {canToggleCategory && (
                              <button
                                type="button"
                                className={`category-status-toggle ${item?.status ? "active" : "inactive"}`}
                                aria-label={`${item?.status ? t("category.statusActive") : t("category.statusInactive")} - ${languageFields(item, "name", i18n)}`}
                                onClick={() => handleToggleStatus(item)}
                              >
                                <span className="category-status-toggle-icon" aria-hidden="true">
                                  {item?.status ? <Play size={11} fill="currentColor" /> : <Pause size={11} fill="currentColor" />}
                                </span>
                                {item?.status ? t("category.statusActive") : t("category.statusInactive")}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td colSpan={canManageCategory ? 5 : 4}>
                    <EmptyList name={t("category.emptyListName")} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      {totalCategories > 0 && (
        <div className="pagination-wrapper category-pagination">
          <CustomPagination
            total={totalCategories}
            onPageChange={(page, rows) => { setCurrentPage(page); setPerPage(rows); }}
            resetPage={resetPage}
          />
        </div>
      )}

      <AddCategoryModal
        open={addCatOpen}
        onClose={() => setAddCatOpen(false)}
        onSaved={fetchCategories}
      />

      <AddSubCategoryModal
        open={addSubOpen}
        onClose={() => setAddSubOpen(false)}
        onSaved={fetchCategories}
        allCategories={categoriesList || []}
      />

      <CategoryDetails
        open={!!editCategory}
        editCategory={editCategory}
        currentPage={currentPage}
        perPage={perPage}
        onClose={() => setEditCategory(null)}
        onSaved={fetchCategories}
      />
    </div>
  );
};

export default Category;
